/**
 * Platform Utilities
 *
 * Centralized utilities for social media platform branding,
 * icons, colors, and character limits.
 *
 * @author AI Assistant
 * @created 2024
 */

import React from 'react';
import {
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    LinkedIn as LinkedInIcon,
    Twitter as TwitterIcon,
} from '@mui/icons-material';
import TikTokLogo from "../components/TikTokLogo";
import ThreadsLogo from "../components/ThreadsLogo";
import BlueskyLogo from "../components/BlueskyLogo";
import MastodonLogo from "../components/MastodonLogo";

/**
 * Get platform icon component function (for use with React.createElement)
 * @param {string} platform - Platform name
 * @returns {React.ComponentType} Icon component function
 */
export const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
        case 'twitter':
            return TwitterIcon;
        case 'facebook':
            return FacebookIcon;
        case 'instagram':
            return InstagramIcon;
        case 'linkedin':
            return LinkedInIcon;
        case 'linkedin_org':
            return LinkedInIcon;
        case 'tiktok':
            return TikTokLogo;
        case 'threads':
            return ThreadsLogo;
        case 'bluesky':
            return BlueskyLogo;
        case 'mastodon':
            return MastodonLogo;
        default:
            return TwitterIcon; // Use Twitter icon as fallback
    }
};

/**
 * Get platform icon as JSX element (for direct JSX usage)
 * @param {string} platform - Platform name
 * @param {object} props - Additional props for the icon
 * @returns {React.Element} Icon JSX element
 */
export const getPlatformIconJSX = (platform, props = {}) => {
    const iconProps = {fontSize: 'small', ...props};

    switch (platform?.toLowerCase()) {
        case 'twitter':
            return <TwitterIcon {...iconProps} />;
        case 'facebook':
            return <FacebookIcon {...iconProps} />;
        case 'instagram':
            return <InstagramIcon {...iconProps} />;
        case 'linkedin':
            return <LinkedInIcon  {...iconProps} />;
        case 'linkedin_org':
            return <LinkedInIcon {...iconProps} />;
        case 'tiktok':
            return <TikTokLogo {...iconProps} />;
        case 'threads':
            return <ThreadsLogo {...iconProps} />;
        case 'bluesky':
            return <BlueskyLogo {...iconProps} />;
        case 'mastodon':
            return <MastodonLogo {...iconProps} />;
        default:
            return <TwitterIcon {...iconProps} />; // Use Twitter icon as fallback
    }
};

/**
 * Get platform color (fallback without theme)
 * @param {string} platform - Platform name (or theme if first param is theme object)
 * @param {string} [platformName] - Platform name when first param is theme
 * @returns {string} Platform color
 */
export const getPlatformColor = (platform, platformName) => {
    // Handle both old (theme, platform) and new (platform) signatures
    const actualPlatform = platformName || platform;

    // Fallback color mapping without theme dependency
    const platformColors = {
        twitter: '#1DA1F2',
        facebook: '#4267B2',
        instagram: '#E4405F',
        linkedin: '#0077B5',
        linkedinorg: '#0077B5',
        tiktok: '#000000',
        threads: '#000000',
        bluesky: '#0085ff',
        mastodon: '#563ACC',
    };

    return platformColors[actualPlatform?.toLowerCase()] || '#9E9E9E';
};

/**
 * Check if platform supports carousel posts
 * @param {string} platform - Platform name
 * @returns {boolean} Whether platform supports carousels
 */
export const supportsCarousel = (platform) => {
    const carouselPlatforms = ['instagram', 'tiktok'];
    return carouselPlatforms.includes(platform?.toLowerCase());
};

/**
 * Get carousel limits for platform
 * @param {string} platform - Platform name
 * @returns {object} Carousel limits
 */
export const getCarouselLimits = (platform) => {
    const limits = {
        instagram: {
            minItems: 2,
            maxItems: 10,
            supportsCaptions: true,
            supportsIndividualCaptions: true,
            allowedTypes: ['image', 'video'],
            maxVideoDuration: 60, // seconds
            aspectRatio: '1:1' // preferred
        },
        tiktok: {
            minItems: 2,
            maxItems: 35,
            supportsCaptions: false,
            supportsIndividualCaptions: false,
            allowedTypes: ['image'],
            maxVideoDuration: 0, // images only for carousel
            aspectRatio: '9:16' // preferred
        }
    };

    return limits[platform?.toLowerCase()] || null;
};

/**
 * Get platform character limits
 * @param {string} platform - Platform name
 * @returns {object} Platform limits and metadata
 */
export const getPlatformLimits = (platform) => {
    const limits = {
        twitter: {
            maxLength: 280,
            name: 'Twitter',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 4,
            supportsCarousel: false
        },
        facebook: {
            maxLength: 2000,
            name: 'Facebook',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 10,
            supportsCarousel: false
        },
        instagram: {
            maxLength: 2200,
            name: 'Instagram',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 10,
            requiresMedia: true,
            supportsCarousel: true,
            carouselLimits: getCarouselLimits('instagram')
        },
        linkedin: {
            maxLength: 3000,
            name: 'LinkedIn',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 9,
            supportsCarousel: false
        },
        linkedinOrg: {
            maxLength: 3000,
            name: 'LinkedIn Organization',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 9,
            supportsCarousel: false
        },
        tiktok: {
            maxLength: 2200,
            name: 'TikTok',
            supportsImages: true,
            supportsVideo: true,
            maxImages: 35,
            supportsCarousel: true,
            carouselLimits: getCarouselLimits('tiktok')
        }
    };

    return limits[platform?.toLowerCase()] || {
        maxLength: 280,
        name: 'Unknown',
        supportsImages: false,
        supportsVideo: false,
        maxImages: 0,
        supportsCarousel: false
    };
};

/**
 * Get platform display name
 * @param {string} platform - Platform identifier
 * @returns {string} Human-readable platform name
 */
export const getPlatformDisplayName = (platform) => {
    const names = {
        twitter: 'Twitter',
        facebook: 'Facebook',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        linkedinOrg: 'LinkedIn Organization',
        tiktok: 'TikTok',
        threads: 'Threads',
        bluesky: 'BlueSky',
        mastodon: 'Mastodon',
    };

    return names[platform?.toLowerCase()] || platform;
};

/**
 * Validate carousel items for platform
 * @param {Array} items - Carousel items
 * @param {string} platform - Platform name
 * @returns {object} Validation result
 */
export const validateCarouselItems = (items, platform) => {
    const carouselLimits = getCarouselLimits(platform);

    if (!carouselLimits) {
        return {
            isValid: false,
            errors: [`${platform} does not support carousel posts`]
        };
    }

    const errors = [];

    // Check item count
    if (items.length < carouselLimits.minItems) {
        errors.push(`Minimum ${carouselLimits.minItems} items required for ${platform} carousel`);
    }

    if (items.length > carouselLimits.maxItems) {
        errors.push(`Maximum ${carouselLimits.maxItems} items allowed for ${platform} carousel`);
    }

    // Check item types
    items.forEach((item, index) => {
        if (!carouselLimits.allowedTypes.includes(item.type)) {
            errors.push(`Item ${index + 1}: ${item.type} not supported for ${platform} carousel`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Get character count color based on limit ratio
 * @param {number} count - Current character count
 * @param {number} limit - Character limit
 * @returns {string} MUI color key
 */
export const getCharacterCountColor = (count, limit) => {
    const ratio = count / limit;
    if (ratio > 1) return 'error';
    if (ratio > 0.9) return 'warning';
    return 'success';
};

/**
 * Format post type label
 * @param {string} type - Post type identifier
 * @returns {string} Human-readable post type
 */
export const getPostTypeLabel = (type) => {
    if (!type) return '';

    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * Get status color for chips
 * @param {string} status - Post/campaign status
 * @returns {string} MUI color key
 */
export const getStatusColor = (status) => {
    const statusColors = {
        draft: 'default',
        scheduled: 'warning',
        published: 'success',
        posted: 'success',
        failed: 'error',
        error: 'error',
        active: 'info',
        completed: 'success',
        ready: 'warning',
    };

    return statusColors[status?.toLowerCase()] || 'default';
}; 